(function () {
  const MM_TO_PX = 96 / 25.4;
  const SIDE_CLASSES = [
    'restaurant-menu__print-page--left',
    'restaurant-menu__print-page--right'
  ];

  function localFontAvailable(fontFamily) {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const sample = 'mmmmmmmmmwwwwwiiiiiiAgencyFB1234567890';
    const fallbacks = ['monospace', 'serif', 'sans-serif'];

    if (!context) return false;

    return fallbacks.some(function (fallback) {
      context.font = '72px ' + fallback;
      const fallbackWidth = context.measureText(sample).width;
      context.font = '72px "' + fontFamily + '", ' + fallback;
      return Math.abs(context.measureText(sample).width - fallbackWidth) > 1;
    });
  }

  function getPrintRequest() {
    const parameters = new URLSearchParams(window.location.search);
    const format = parameters.get('menu_pdf');
    const requestedStock = parameters.get('stock') || 'all';
    const requestedPlate = parameters.get('plate') || 'composite';
    const stocks = ['all', 'standard', 'dark'];
    const plates = ['composite', 'white', 'accent'];

    if (format !== 'a4' && format !== 'online') return null;

    return {
      autoPrint: parameters.get('auto_print') === '1',
      format: format,
      plate:
        format === 'online'
          ? 'composite'
          : plates.includes(requestedPlate)
            ? requestedPlate
            : 'composite',
      stock:
        format === 'online'
          ? 'all'
          : stocks.includes(requestedStock)
            ? requestedStock
            : 'all'
    };
  }

  function numericData(element, key, fallback) {
    const value = Number.parseFloat(element.dataset[key]);
    return Number.isFinite(value) ? value : fallback;
  }

  function integerData(element, key, fallback) {
    const value = Number.parseInt(element.dataset[key], 10);
    return Number.isFinite(value) ? value : fallback;
  }

  function pageSide(pageIndex, firstSide) {
    const firstPageIsRight = firstSide !== 'left';
    const isRight = pageIndex % 2 === 0 ? firstPageIsRight : !firstPageIsRight;
    return isRight ? 'right' : 'left';
  }

  function chapterMeta(chapter) {
    const logo = chapter.querySelector('[data-menu-print-logo]');
    return {
      dividerStyle: chapter.dataset.dividerStyle || 'none',
      startNewPage: chapter.dataset.startNewPage === 'true',
      startNewColumn: chapter.dataset.startNewColumn === 'true',
      columns: Math.min(3, Math.max(1, integerData(chapter, 'printColumns', 1))),
      logo: logo ? logo.content.querySelector('img') : null,
      runningHeader: chapter.dataset.printHeader === 'running',
      runningTitle: chapter.dataset.printHeader === 'running' ? chapter.querySelector('.restaurant-menu__chapter-title')?.textContent.trim() : '',
      runningCopy: chapter.dataset.printHeader === 'running' ? Array.from(chapter.querySelectorAll('.restaurant-menu__chapter-kicker, .restaurant-menu__chapter-introduction')).map(e => e.textContent.trim()).join(' — ') : '',
      compact: chapter.dataset.printCompact === 'true',
      spacing: chapter.dataset.printSpacing || 'default',
      stock: chapter.dataset.printStock || 'standard',
      theme: chapter.dataset.chapterTheme || 'day'
    };
  }

  function pageElement(content) {
    return content ? content.closest('.restaurant-menu__print-page') : null;
  }

  function pageMatches(content, meta) {
    const page = pageElement(content);

    return Boolean(
      page &&
        page.dataset.dividerPage !== 'true' &&
        (meta.columns === 1
          ? page.dataset.chapterTheme === meta.theme
          : (page.dataset.chapterTheme === 'night') === (meta.theme === 'night')) &&
        page.dataset.printStock === meta.stock &&
        page.dataset.printColumns === String(meta.columns) &&
        page.dataset.printLogo === (meta.logo ? meta.logo.src : '') &&
        page.dataset.printCompact === String(Boolean(meta.compact)) &&
        page.dataset.printSpacing === (meta.spacing || 'default') &&
        (page.dataset.printHeader === 'running') === Boolean(meta.runningHeader) &&
        (!meta.runningHeader || page.dataset.runningTitle === (meta.runningTitle || ''))
    );
  }

  function createPage(output, source, request, meta) {
    const pageIndex = output.children.length;
    const page = document.createElement('div');
    const content = document.createElement('div');
    const mirroredMargins = source.dataset.mirroredMargins === 'true';
    const typography = source.dataset.typographyStyle || 'condensed';

    page.className = [
      'restaurant-menu__print-page',
      'restaurant-menu__print-page--' + request.format,
      'restaurant-menu__print-page--theme-' + meta.theme,
      'restaurant-menu__print-page--stock-' + meta.stock,
      'restaurant-menu__print-page--typography-' + typography
    ].join(' ');
    page.style.cssText = source.style.cssText;
    page.dataset.chapterTheme = meta.theme;
    page.dataset.printStock = meta.stock;
    page.dataset.printColumns = String(meta.columns || 1);
    page.dataset.hasPrintProfile = source.dataset.hasPrintProfile || 'false';
    page.dataset.printMenu = source.dataset.menuPrintMenu || '';
    page.dataset.printLogo = meta.logo ? meta.logo.src : '';
    page.dataset.dividerPage = meta.dividerPage ? 'true' : 'false';
    page.dataset.printCompact = String(Boolean(meta.compact));
    page.dataset.printSpacing = meta.spacing || 'default';
    if (meta.runningHeader) page.dataset.printHeader = 'running';
    page.dataset.runningTitle = meta.runningTitle || '';
    const appearance = source.querySelector('[data-menu-output-presentation]')?.content.firstElementChild;
    if (appearance) {
      Array.from(appearance.attributes).forEach(function (attribute) {
        if (attribute.name.startsWith('data-menu-')) page.setAttribute(attribute.name, attribute.value);
      });
      Array.from(appearance.style).forEach(function (property) {
        page.style.setProperty(property, appearance.style.getPropertyValue(property));
      });
    }

    if (mirroredMargins) {
      page.classList.add(
        'restaurant-menu__print-page--' +
          pageSide(pageIndex, source.dataset.firstPageSide || 'right')
      );
    } else {
      page.classList.add('restaurant-menu__print-page--right');
    }

    content.className = 'restaurant-menu__print-page-content';
    page.appendChild(content);
    output.appendChild(page);

    if (meta.columns > 1 && !meta.dividerPage) {
      page.classList.add('restaurant-menu__print-page--column-layout');
      const header = document.createElement('header');
      header.className = 'restaurant-menu__print-running-header';
      // An optional menu masthead replaces the header logo on the first page.
      const masthead = pageIndex === 0 ? source.querySelector('[data-menu-print-masthead]') : null;
      if (masthead && masthead.content.firstElementChild) {
        header.appendChild(masthead.content.firstElementChild.cloneNode(true));
        page.classList.add('restaurant-menu__print-page--masthead');
      } else if (meta.logo) {
        header.appendChild(meta.logo.cloneNode(true));
      }
      if (meta.runningTitle) {
        const title = document.createElement('h2');
        title.className = 'restaurant-menu__print-running-title';
        title.textContent = meta.runningTitle;
        header.appendChild(title);
      }
      const summary = document.createElement('p');
      summary.className = 'restaurant-menu__print-page-summary';
      header.appendChild(summary);
      if (meta.runningHeader && meta.runningCopy) {
        const copy = document.createElement('p');
        copy.className = 'restaurant-menu__print-running-copy';
        copy.textContent = meta.runningCopy;
        header.appendChild(copy);
      }
      content.appendChild(header);
      const columns = document.createElement('div');
      columns.className = 'restaurant-menu__print-columns';
      columns.style.setProperty('--menu-print-flow-columns', meta.columns);
      content.appendChild(columns);
      for (let index = 0; index < meta.columns; index += 1) {
        const column = document.createElement('div');
        column.className = 'restaurant-menu__print-column';
        if (source.dataset.hasPrintProfile === 'true') {
          ['item-gap', 'section-gap', 'chapter-header-gap'].forEach(function (property) {
            column.style.setProperty('--menu-print-' + property, 'inherit');
          });
        }
        columns.appendChild(column);
      }
      return columns.firstElementChild;
    }

    return content;
  }

  function overflowTolerance(source) {
    return numericData(source, 'overflowToleranceMm', 4) * MM_TO_PX;
  }

  function fitsOnPage(content, source) {
    content.querySelectorAll('.restaurant-menu__item:has(.restaurant-menu__item-label)').forEach(function (row) {
      if (!row.closest('.restaurant-menu__print-page--stock-dark')) return;
      const name = row.querySelector('.restaurant-menu__item-name');
      const offset = name.getBoundingClientRect().top - row.getBoundingClientRect().top;
      row.querySelectorAll('.restaurant-menu__item-value').forEach(function (value) {
        const leading = (parseFloat(getComputedStyle(name).lineHeight) - parseFloat(getComputedStyle(value).lineHeight)) / 2;
        value.style.setProperty('padding-top', Math.max(0, offset + leading) + 'px', 'important');
      });
    });
    // Column layouts must not consume the footer or neighbouring columns.
    const tolerance = content.matches('.restaurant-menu__print-column') ? 1 : overflowTolerance(source);
    return content.scrollHeight <= content.clientHeight + tolerance &&
      content.scrollWidth <= content.clientWidth + 1;
  }

  function sectionShell(section, continuation) {
    const clone = section.cloneNode(true);
    const body = clone.querySelector('tbody');

    if (body) body.replaceChildren();
    clone.removeAttribute('id');

    if (continuation) {
      clone.classList.add('restaurant-menu__section--continuation');
    }

    return clone;
  }

  function splitOversizedSection(
    section,
    output,
    source,
    request,
    meta,
    startingContent
  ) {
    const rows = Array.from(section.querySelectorAll('tbody > tr'));
    let content = startingContent;
    let fragment = sectionShell(section, false);
    let fragmentBody = fragment.querySelector('tbody');
    let rowCount = 0;

    content.appendChild(fragment);

    if (rows.length === 0 && !fitsOnPage(content, source)) {
      fragment.remove();
      content = createPage(output, source, request, meta);
      content.appendChild(fragment);
      return;
    }

    rows.forEach(function (row) {
      const rowClone = row.cloneNode(true);
      fragmentBody.appendChild(rowClone);

      if (fitsOnPage(content, source)) {
        rowCount += 1;
        return;
      }

      if (rowCount === 0) {
        if (content.children.length > 1) {
          fragment.remove();
          content = createPage(output, source, request, meta);
          fragment = sectionShell(section, false);
          fragmentBody = fragment.querySelector('tbody');
          fragmentBody.appendChild(rowClone);
          content.appendChild(fragment);
        }

        rowCount += 1;
        return;
      }

      rowClone.remove();
      content = createPage(output, source, request, meta);
      fragment = sectionShell(section, true);
      fragmentBody = fragment.querySelector('tbody');
      fragmentBody.appendChild(rowClone);
      content.appendChild(fragment);
      rowCount = 1;
    });
  }

  function appendSection(
    section,
    state,
    output,
    source,
    request,
    meta,
    ignoreStartNewPage
  ) {
    if (
      !ignoreStartNewPage &&
      (section.dataset.startNewPage === 'true' || section.dataset.startNewColumn === 'true') &&
      state.content &&
      state.content.children.length > 0
    ) {
      state.content = createPage(output, source, request, meta);
    }

    const clone = section.cloneNode(true);
    state.content.appendChild(clone);

    if (fitsOnPage(state.content, source)) return;

    clone.remove();

    const onlyChapterHeader =
      state.content.children.length === 1 &&
      state.content.firstElementChild.matches('[data-menu-chapter-header]');

    if (state.content.children.length > 0 && !onlyChapterHeader) {
      state.content = createPage(output, source, request, meta);
    }

    const nextPageClone = section.cloneNode(true);
    state.content.appendChild(nextPageClone);

    if (fitsOnPage(state.content, source)) return;

    nextPageClone.remove();
    splitOversizedSection(
      section,
      output,
      source,
      request,
      meta,
      state.content
    );
    state.content = output.lastElementChild.querySelector(
      '.restaurant-menu__print-page-content'
    );
  }

  function appendChapter(chapter, state, output, source, request) {
    const meta = chapterMeta(chapter);
    const header = chapter.querySelector('[data-menu-chapter-header]');
    const sections = Array.from(chapter.querySelectorAll('[data-menu-section]'));
    let firstSectionStartHandled = false;

    if (
      !state.content ||
      meta.startNewPage || meta.startNewColumn ||
      !pageMatches(state.content, meta)
    ) {
      state.content = createPage(output, source, request, meta);
    }

    if (meta.dividerStyle === 'full-page' && header) {
      if (state.content.children.length > 0) {
        state.content = createPage(output, source, request, meta);
      }

      state.content.appendChild(header.cloneNode(true));

      if (sections.length > 0) {
        state.content = createPage(output, source, request, meta);
        firstSectionStartHandled = true;
      }
    } else if (header) {
      const headerClone = header.cloneNode(true);
      const firstSectionStartsPage =
        sections.length > 0 &&
        sections[0].dataset.startNewPage === 'true';

      if (firstSectionStartsPage && state.content.children.length > 0) {
        state.content = createPage(output, source, request, meta);
      }
      firstSectionStartHandled = firstSectionStartsPage;

      if (sections.length > 0 && state.content.children.length > 0) {
        const firstSectionClone = sections[0].cloneNode(true);
        state.content.appendChild(headerClone);
        state.content.appendChild(firstSectionClone);

        if (fitsOnPage(state.content, source)) {
          sections.slice(1).forEach(function (section) {
            appendSection(section, state, output, source, request, meta);
          });
          return;
        }

        headerClone.remove();
        firstSectionClone.remove();
        state.content = createPage(output, source, request, meta);
      }

      const finalHeaderClone = header.cloneNode(true);
      state.content.appendChild(finalHeaderClone);

      if (
        !fitsOnPage(state.content, source) &&
        state.content.children.length > 1
      ) {
        finalHeaderClone.remove();
        state.content = createPage(output, source, request, meta);
        state.content.appendChild(header.cloneNode(true));
      }
    }

    sections.forEach(function (section, sectionIndex) {
      appendSection(
        section,
        state,
        output,
        source,
        request,
        meta,
        sectionIndex === 0 && firstSectionStartHandled
      );
    });
  }

  function nextColumn(content, output, source, request, meta) {
    if (!content.nextElementSibling && content.parentElement.dataset.boundedRow === 'true') {
      throw new Error('The print row does not fit after ' +
        (content.lastElementChild?.dataset.menuSectionTitle || 'this section') +
        ' (' + content.clientHeight + 'px available). Add a page break or shorten its content.');
    }
    return content.nextElementSibling || createPage(output, source, request, meta);
  }

  function pageHasContent(content) {
    return Boolean(pageElement(content).querySelector('[data-menu-section], [data-menu-chapter-header]'));
  }

  function columnBreak(section, state, output, source, request, meta) {
    const dedicated = section.dataset.printPresentation === 'premium-column';
    if (section.dataset.startNewPage === 'true' && pageHasContent(state.content)) {
      state.content = createPage(output, source, request, meta);
    }
    if (section.dataset.startNewRow === 'true') {
      const row = state.content.parentElement;
      const top = row.getBoundingClientRect().top;
      const used = Math.max(...Array.from(row.children).map(column => Math.max(top, ...Array.from(column.children).map(child => child.getBoundingClientRect().bottom)))) - top;
      const available = row.clientHeight - used - 3 * MM_TO_PX;
      if (available <= 0) throw new Error('No space remains for the print row. Add a page break.');
      {
        row.style.flex = '0 0 ' + used + 'px';
        row.style.height = used + 'px';
        row.style.marginBottom = used ? '3mm' : '0';
        const next = row.cloneNode(false);
        next.dataset.boundedRow = 'true';
        next.style.flex = '1';
        next.style.height = '';
        next.style.marginBottom = '';
        const count = Math.min(3, Math.max(2, integerData(section, 'printRowColumns', meta.columns)));
        next.style.setProperty('--menu-print-flow-columns', count);
        next.style.gap = count === 3 ? '6mm' : '12mm';
        const groupSpan = Math.min(meta.columns, Math.max(0, integerData(section, 'menuGroupPrintSpan', 0)));
        if (groupSpan > 0) {
          next.dataset.menuGroupSpan = String(groupSpan);
          next.style.setProperty('--menu-group-span', groupSpan);
        }
        for (let index = 0; index < count; index += 1) next.appendChild(row.firstElementChild.cloneNode(false));
        row.after(next);
        if (section.dataset.printRowHeading === 'true') {
          const heading = document.createElement('div');
          heading.className = 'restaurant-menu__print-row-heading';
          if (section.dataset.menuGroupDecoration) heading.dataset.groupDecoration = section.dataset.menuGroupDecoration;
          if (groupSpan > 0) {
            heading.dataset.menuGroupSpan = String(groupSpan);
            heading.style.setProperty('--menu-group-span', groupSpan);
          }
          const title = document.createElement('h3');
          title.textContent = section.dataset.menuSectionTitle || '';
          const intro = document.createElement('p');
          intro.textContent = section.dataset.printRowIntroduction || '';
          heading.append(title, intro);
          next.before(heading);
        }
        state.content = next.firstElementChild;
      }
      return;
    }
    if (section.dataset.startNewPage !== 'true' && (section.dataset.startNewColumn === 'true' || dedicated) && state.content.children.length) {
      state.content = nextColumn(state.content, output, source, request, meta);
    }
  }

  function appendColumnSection(section, state, output, source, request, meta) {
    const whole = section.cloneNode(true);
    whole.removeAttribute('id');
    state.content.appendChild(whole);
    if (fitsOnPage(state.content, source)) return;
    whole.remove();

    if (section.dataset.printPresentation === 'premium-column') {
      throw new Error('A premium section must fit its dedicated print column: ' + section.dataset.menuSectionTitle);
    }

    // Keep attached amendments and the final footnote with their parent row.
    const groups = [];
    Array.from(section.querySelectorAll('tbody > tr')).forEach(function (row) {
      const attached = row.matches('.restaurant-menu__item--tuck-under, .restaurant-menu__item--layout-sub-item, [data-menu-footnote], [data-menu-print-copy]');
      const previous = groups.length ? groups[groups.length - 1].at(-1) : null;
      const followsHeading = previous?.matches('[data-menu-subheading]');
      const sameFrame = row.dataset.menuEmphasisGroup && previous?.dataset.menuEmphasisGroup === row.dataset.menuEmphasisGroup;
      if (groups.length && (attached || followsHeading || sameFrame)) groups[groups.length - 1].push(row);
      else groups.push([row]);
    });
    if (!groups.length) groups.push([]);

    // Short callouts should not be split into a heading and a lone option.
    if (groups.length <= 2 && state.content.querySelector('[data-menu-section]')) {
      const trailingHeader = state.content.lastElementChild?.matches('[data-menu-chapter-header]') ? state.content.lastElementChild : null;
      state.content = nextColumn(state.content, output, source, request, meta);
      if (trailingHeader) state.content.appendChild(trailingHeader);
      state.content.appendChild(whole);
      if (fitsOnPage(state.content, source)) return;
      whole.remove();
    }

    let fragment = sectionShell(section, false);
    let body = fragment.querySelector('tbody');
    let placed = 0;
    state.content.appendChild(fragment);
    groups.forEach(function (group) {
      const clones = group.map(function (row) { return row.cloneNode(true); });
      clones.forEach(function (row) { body.appendChild(row); });
      if (!fitsOnPage(state.content, source)) {
        clones.forEach(function (row) { row.remove(); });
        let orphanHeader = null;
        let carriedRows = [];
        if (placed === 1 && state.content.children.length > 1) {
          carriedRows = Array.from(body.children);
          fragment.remove();
        }
        if (placed === 0) {
          fragment.remove();
          // A chapter heading must travel with its first item, not sit alone.
          if (state.content.lastElementChild && state.content.lastElementChild.matches('[data-menu-chapter-header]')) {
            orphanHeader = state.content.lastElementChild;
            orphanHeader.remove();
          }
          if (!state.content.children.length) {
            if (orphanHeader) state.content.appendChild(orphanHeader);
            state.content.appendChild(fragment);
            clones.forEach(function (row) { body.appendChild(row); });
            throw new Error('A menu item group is taller or wider than a print column: ' + section.dataset.menuSectionTitle);
          }
        }
        state.content = nextColumn(state.content, output, source, request, meta);
        if (orphanHeader) state.content.appendChild(orphanHeader);
        fragment = sectionShell(section, placed > 0 && !carriedRows.length);
        body = fragment.querySelector('tbody');
        carriedRows.forEach(function (row) { body.appendChild(row); });
        clones.forEach(function (row) { body.appendChild(row); });
        state.content.appendChild(fragment);
        if (!fitsOnPage(state.content, source)) {
          throw new Error('A menu item group does not fit a print column: ' + section.dataset.menuSectionTitle);
        }
      }
      placed += 1;
    });
  }

  function columnSection(sourceSection) {
    const section = sourceSection.cloneNode(true);
    const compact = sourceSection.closest('[data-menu-chapter]')?.dataset.printCompact === 'true';
    if (section.dataset.printRowHeading === 'true') {
      section.dataset.printRowIntroduction = section.querySelector('.restaurant-menu__section-introduction')?.textContent.trim() || '';
    }
    if (section.dataset.printHideHeading === 'true' || section.dataset.printRowHeading === 'true') {
      section.querySelector('thead')?.remove();
    }
    const columnCount = section.querySelectorAll('col').length;
    if (columnCount === 2) {
      const priceRow = section.querySelector('.restaurant-menu__section-heading-row--values');
      const price = priceRow && priceRow.querySelector('.restaurant-menu__column-heading--price');
      if (price) {
        const title = section.querySelector('.restaurant-menu__section-title');
        const inlinePrice = document.createElement('span');
        inlinePrice.className = 'restaurant-menu__print-heading-price';
        while (price.firstChild) inlinePrice.appendChild(price.firstChild);
        title.querySelector('.restaurant-menu__section-title-text').prepend(inlinePrice);
        title.classList.add('restaurant-menu__section-title--with-price');
        priceRow.remove();
      }
    }
    section.querySelectorAll('[data-menu-item]').forEach(function (row) {
      const values = Array.from(row.querySelectorAll('.restaurant-menu__item-value'));
      const information = row.querySelector('.restaurant-menu__item-information');
      const expandedEmphasis = row.matches('.restaurant-menu__item--emphasis-featured') ||
        (row.matches('.restaurant-menu__item--emphasis-hero') && values[0]?.querySelector('.restaurant-menu__price-suffix'));
      if (compact && expandedEmphasis && information && values.length === 1 && values[0].textContent.trim()) {
        const copy = Array.from(information.querySelectorAll('.restaurant-menu__item-supporting, .restaurant-menu__item-description'));
        if (copy.length) {
          const detail = document.createElement('tr');
          detail.className = 'restaurant-menu__print-copy-row ' + (row.matches('.restaurant-menu__item--emphasis-featured')
            ? 'restaurant-menu__print-copy-row--featured'
            : 'restaurant-menu__print-copy-row--hero');
          detail.dataset.menuPrintCopy = 'true';
          const cell = document.createElement('td');
          cell.colSpan = columnCount;
          copy.forEach(function (element) { cell.appendChild(element); });
          detail.appendChild(cell);
          row.after(detail);
          row.classList.add('restaurant-menu__item--has-print-copy');
        }
      } else if (compact && information && !row.matches('.restaurant-menu__item--emphasis-standard') && values.length === 1 && values[0].textContent.trim()) {
        const price = document.createElement('span');
        price.className = 'restaurant-menu__compact-price';
        while (values[0].firstChild) price.appendChild(values[0].firstChild);
        row.querySelector('.restaurant-menu__item-name').prepend(price);
        information.colSpan = columnCount;
        values[0].remove();
      }
      if (values.length && values.every(function (value) { return !value.textContent.trim(); })) {
        information.colSpan = columnCount;
        values.forEach(function (value) { value.remove(); });
      }
      // Descriptions span the reading column, while names and prices stay aligned.
      // Featured and Hero rows use the expanded copy rows above; preserve other
      // established feature panels and emphasis bars unchanged.
      if (!row.matches('.restaurant-menu__item--emphasis-standard') || information.colSpan > 1 || row.dataset.menuEmphasisGroup) return;
      const copy = Array.from(information.querySelectorAll('.restaurant-menu__item-supporting, .restaurant-menu__item-description'));
      if (!copy.length) return;
      const detail = document.createElement('tr');
      detail.className = 'restaurant-menu__print-copy-row';
      detail.dataset.menuPrintCopy = 'true';
      const cell = document.createElement('td');
      cell.colSpan = columnCount;
      copy.forEach(function (element) { cell.appendChild(element); });
      detail.appendChild(cell);
      row.after(detail);
      row.classList.add('restaurant-menu__item--has-print-copy');
    });
    section.querySelectorAll('.restaurant-menu__section-heading-row--values').forEach(function (row) {
      if (!Array.from(row.querySelectorAll('.restaurant-menu__column-heading')).some(function (cell) {
        return Array.from(cell.childNodes).some(function (node) {
          return node.nodeType === 3 ? node.textContent.trim() : !node.classList.contains('visually-hidden');
        });
      })) row.remove();
    });
    return section;
  }

  function appendColumnChapter(chapter, state, output, source, request) {
    const meta = chapterMeta(chapter);
    const header = chapter.querySelector('[data-menu-chapter-header]');
    const sections = Array.from(chapter.querySelectorAll('[data-menu-section]')).map(columnSection);
    if (meta.dividerStyle === 'full-page' && header && !meta.runningHeader) {
      const divider = createPage(output, source, request, Object.assign({}, meta, { dividerPage: true }));
      divider.appendChild(header.cloneNode(true));
      state.content = null;
    }
    if (!sections.length) return;
    if (!state.content || meta.startNewPage || !pageMatches(state.content, meta)) {
      state.content = createPage(output, source, request, meta);
    } else if (meta.startNewColumn && state.content.children.length) {
      state.content = nextColumn(state.content, output, source, request, meta);
    }
    sections.forEach(function (section, index) {
      columnBreak(section, state, output, source, request, meta);
      if (index === 0 && header && meta.dividerStyle !== 'full-page' && !meta.runningHeader) {
        state.content.appendChild(header.cloneNode(true));
      }
      appendColumnSection(section, state, output, source, request, meta);
    });
  }

  function updatePageSummaries(output) {
    output.querySelectorAll('.restaurant-menu__print-page').forEach(function (page) {
      const summary = page.querySelector('.restaurant-menu__print-page-summary');
      if (!summary) return;
      const titles = Array.from(page.querySelectorAll('[data-menu-section]')).map(function (section) {
        return section.dataset.menuSummaryLabel || section.dataset.menuSectionTitle;
      }).filter(Boolean);
      summary.textContent = Array.from(new Set(titles)).join(' · ');
      if (summary.scrollHeight > summary.clientHeight + 1) {
        throw new Error('The page summary is too long for its header. Add a new page break.');
      }
    });
  }

  function appendDietaryKey(container, destination) {
    const markers = Array.from(container.querySelectorAll('[data-menu-dietary]'));
    if (!markers.length || destination.querySelector(':scope > .restaurant-menu__dietary-key')) return;
    const key = document.createElement('p');
    key.className = 'restaurant-menu__dietary-key';
    const seen = new Set();
    markers.forEach(function (marker) {
      if (seen.has(marker.dataset.menuDietary)) return;
      seen.add(marker.dataset.menuDietary);
      const entry = document.createElement('span');
      const symbol = document.createElement('span');
      symbol.className = 'restaurant-menu__dietary-marker';
      symbol.textContent = marker.textContent;
      entry.append(symbol, ' ' + marker.dataset.menuDietaryLabel);
      key.appendChild(entry);
    });
    destination.appendChild(key);
    const page = destination.closest('.restaurant-menu__print-page');
    if (page) {
      const bounds = key.getBoundingClientRect();
      const pageBounds = page.getBoundingClientRect();
      if (bounds.bottom > pageBounds.bottom - MM_TO_PX || bounds.right > pageBounds.right - MM_TO_PX) {
        throw new Error('The dietary key needs a larger bottom margin in the print profile.');
      }
    }
  }

  function paginate(source, output, request) {
    const chapters = Array.from(
      source.querySelectorAll(':scope > [data-menu-chapter]')
    );
    const state = { content: null };

    chapters.forEach(function (chapter) {
      if (chapterMeta(chapter).columns > 1) appendColumnChapter(chapter, state, output, source, request);
      else appendChapter(chapter, state, output, source, request);
    });
    updatePageSummaries(output);
    output.querySelectorAll('.restaurant-menu__print-page').forEach(function (page) {
      appendDietaryKey(page, page.querySelector('.restaurant-menu__print-page-content'));
    });
  }

  function removeEmptyPages(output) {
    Array.from(output.children).forEach(function (page) {
      const content = page.querySelector('.restaurant-menu__print-page-content');

      if (!content || !content.querySelector('[data-menu-section], [data-menu-chapter-header]')) {
        page.remove();
      }
    });
  }

  function padToPageMultiple(output, source, request) {
    const requestedMultiple = integerData(
      source,
      'pageMultiple',
      1
    );
    const pageMultiple = Math.min(Math.max(requestedMultiple, 1), 64);
    const remainder = output.children.length % pageMultiple;
    const blankPagesNeeded = remainder === 0 ? 0 : pageMultiple - remainder;
    const blankMeta = { stock: 'standard', theme: 'plain' };

    for (let index = 0; index < blankPagesNeeded; index += 1) {
      const content = createPage(output, source, request, blankMeta);
      const page = pageElement(content);
      page.classList.add('restaurant-menu__print-page--blank');
      page.dataset.blankPage = 'true';
    }
  }

  function normalizePageSides(output, source) {
    const mirroredMargins = source.dataset.mirroredMargins === 'true';
    const firstSide = source.dataset.firstPageSide || 'right';

    Array.from(output.children).forEach(function (page, pageIndex) {
      page.classList.remove(...SIDE_CLASSES);
      page.classList.add(
        'restaurant-menu__print-page--' +
          (mirroredMargins ? pageSide(pageIndex, firstSide) : 'right')
      );
    });
  }

  function addPageNumbers(output, source) {
    const position = source.dataset.pageNumberPosition || 'none';

    if (position === 'none') return;

    output.classList.add('restaurant-menu__print-output--folios-' + position);

    Array.from(output.children).forEach(function (page, pageIndex) {
      if (page.dataset.blankPage === 'true') return;

      const folio = document.createElement('span');
      folio.className = 'restaurant-menu__page-number';
      folio.textContent = String(pageIndex + 1);
      folio.setAttribute('aria-hidden', 'true');
      page.appendChild(folio);
    });
  }

  function applyOutputFilter(output, request) {
    output.classList.add('restaurant-menu__print-output--plate-' + request.plate);
    output.classList.add('restaurant-menu__print-output--stock-' + request.stock);

    Array.from(output.children).forEach(function (page) {
      const excluded =
        request.stock !== 'all' && page.dataset.printStock !== request.stock;

      if (excluded) {
        page.classList.add('restaurant-menu__print-page--excluded');
      }
    });
  }

  function injectPageRule(source) {
    const width = numericData(source, 'pageWidthMm', 148);
    const height = numericData(source, 'pageHeightMm', 210);
    const style = document.createElement('style');

    style.id = 'restaurant-menu-dynamic-page-size';
    style.textContent =
      '@page menu-output { size: ' +
      width +
      'mm ' +
      height +
      'mm; margin: 0; }';
    document.head.appendChild(style);
  }

  function filename(title, request, prefix) {
    if (request.format === 'online') {
      return (prefix || 'Full Menu') + ' ' + new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(new Date());
    }

    const formatSuffix = request.format === 'online' ? 'full-menu' : 'a4-menu';
    const parts = [formatSuffix];

    if (request.stock !== 'all') parts.push(request.stock + '-stock');
    if (request.plate !== 'composite') parts.push(request.plate + '-plate');

    return (
      (title || 'menu')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '') +
      '-' +
      parts.join('-')
    );
  }

  let jumpController;

  function jumpMatchIndex(labels, current, query) {
    const start = query.length === 1 ? current + 1 : Math.max(current, 0);
    for (let step = 0; step < labels.length; step += 1) {
      const index = (start + step) % labels.length;
      if (labels[index].trim().toLocaleLowerCase().startsWith(query)) return index;
    }
    return -1;
  }

  function setupJumpNavigation() {
    if (jumpController) jumpController.abort();
    jumpController = new AbortController();
    const listenerOptions = { signal: jumpController.signal };
    document.querySelectorAll('[data-menu-context="screen"] .restaurant-menu__chapter-inner').forEach(function (chapter) {
      appendDietaryKey(chapter, chapter);
    });
    const disclosures = Array.from(document.querySelectorAll('[data-menu-jump]'));

    if (disclosures.length === 0) return;

    const roots = Array.from(
      new Set(
        disclosures
          .map(function (disclosure) {
            return disclosure.closest('[data-restaurant-menu]');
          })
          .filter(Boolean)
      )
    );

    function visibleStickyHeader() {
      return Array.from(document.querySelectorAll('.stickynav')).find(
        function (header) {
          return (
            window.getComputedStyle(header).display !== 'none' &&
            header.getBoundingClientRect().height > 0
          );
        }
      );
    }

    function updateJumpOffset() {
      const header = visibleStickyHeader();
      const offset = header
        ? Math.ceil(header.getBoundingClientRect().height) + 16
        : 16;

      roots.forEach(function (root) {
        root.style.setProperty('--menu-jump-offset', offset + 'px');
      });
    }

    function alignCurrentHash() {
      if (!window.location.hash) return;

      let targetId;

      try {
        targetId = decodeURIComponent(window.location.hash.slice(1));
      } catch (error) {
        return;
      }

      const target = document.getElementById(targetId);

      if (!target || !target.closest('[data-restaurant-menu]')) return;
      target.scrollIntoView({ block: 'start' });
    }

    updateJumpOffset();
    window.addEventListener('resize', updateJumpOffset, listenerOptions);
    window.addEventListener('load', function () {
      updateJumpOffset();
      alignCurrentHash();
    }, listenerOptions);

    disclosures.forEach(function (disclosure) {
      const button = disclosure.querySelector('button');
      const panel = disclosure.querySelector('.restaurant-menu__navigation-panel');
      const links = Array.from(panel.querySelectorAll('a'));
      let prefix = '';
      let typedAt = 0;

      function setOpen(open) {
        panel.hidden = !open;
        button.setAttribute('aria-expanded', String(open));
        if (!open) prefix = '';
      }

      function focusLink(index) {
        if (!links.length) return;
        setOpen(true);
        links[(index + links.length) % links.length].focus();
      }

      button.addEventListener('click', function () {
        setOpen(panel.hidden);
      }, listenerOptions);

      disclosure.addEventListener('keydown', function (event) {
        if (event.ctrlKey || event.metaKey || event.altKey || event.isComposing) return;
        const current = links.indexOf(document.activeElement);
        if (event.key === 'Escape') {
          if (panel.hidden) return;
          event.preventDefault();
          setOpen(false);
          button.focus();
        } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          prefix = '';
          focusLink(current < 0 ? (event.key === 'ArrowDown' ? 0 : links.length - 1) : current + (event.key === 'ArrowDown' ? 1 : -1));
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          prefix = '';
          focusLink(event.key === 'Home' ? 0 : links.length - 1);
        } else if (event.key === ' ' && current >= 0) {
          event.preventDefault();
          links[current].click();
        } else if (event.key.length === 1 && event.key !== ' ') {
          event.preventDefault();
          const now = Date.now();
          const letter = event.key.toLocaleLowerCase();
          prefix = now - typedAt > 1000 ? letter : prefix + letter;
          typedAt = now;
          const repeated = Array.from(prefix).every(function (char) { return char === letter; });
          if (repeated) prefix = letter;
          const index = jumpMatchIndex(links.map(function (link) { return link.textContent; }), current, prefix);
          if (index >= 0) focusLink(index);
        }
      }, listenerOptions);

      panel.addEventListener('click', function (event) {
        const link = event.target.closest('a');
        if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
        const target = document.getElementById(link.hash.slice(1));
        if (!target) return;
        event.preventDefault();
        setOpen(false);
        history.pushState(null, '', link.hash);
        updateJumpOffset();
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
        target.scrollIntoView({ block: 'start' });
      }, listenerOptions);

      disclosure.addEventListener('focusout', function (event) {
        if (!disclosure.contains(event.relatedTarget)) setOpen(false);
      }, listenerOptions);
      document.addEventListener('click', function (event) {
        if (!disclosure.contains(event.target)) setOpen(false);
      }, listenerOptions);
      document.addEventListener('keydown', function (event) {
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'f') setOpen(false);
      }, listenerOptions);
    });
  }

  async function preparePrint(root, request) {
    const source = root.querySelector(
      '[data-menu-print-source="' + request.format + '"]'
    );

    if (!source || source.querySelector('[data-menu-chapter]') === null) {
      window.alert('This menu has no chapters to export.');
      return;
    }

    if (
      source.dataset.typographyStyle === 'condensed' &&
      !localFontAvailable('Agency FB')
    ) {
      window.alert(
        'Agency FB is not installed on this computer. Install the licensed desktop font or select Theme sans/Editorial in the print profile.'
      );
      return;
    }

    document.body.classList.add('restaurant-menu-is-paginating');

    const output = document.createElement('div');
    output.className = 'restaurant-menu__print-output';
    output.style.cssText = source.style.cssText;
    output.setAttribute('aria-hidden', 'true');
    document.body.appendChild(output);

    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }

    try {
      const sourceImages = Array.from(source.querySelectorAll('img')).concat(
        Array.from(source.querySelectorAll('[data-menu-print-logo]')).map(function (template) {
          return template.content.querySelector('img');
        })
      ).filter(Boolean);
      const logoImages = sourceImages.map(function (img) {
        const preload = new Image();
        preload.src = img.src;
        return preload.decode();
      });
      await Promise.all(logoImages);
      paginate(source, output, request);
    } catch (error) {
      output.remove();
      document.body.classList.remove('restaurant-menu-is-paginating');
      const warning = document.createElement('p');
      warning.setAttribute('role', 'alert');
      warning.dataset.menuPrintError = 'true';
      warning.textContent = 'The print menu could not be prepared. ' + error.message;
      root.prepend(warning);
      return;
    }
    removeEmptyPages(output);
    padToPageMultiple(output, source, request);
    normalizePageSides(output, source);
    addPageNumbers(output, source);
    applyOutputFilter(output, request);
    injectPageRule(source);

    const title = root.dataset.a4Title;

    document.title = filename(title, request, source.dataset.menuFilenameTitle);
    document.body.classList.remove('restaurant-menu-is-paginating');
    document.documentElement.classList.add('restaurant-menu-print-mode');
    document.body.classList.add('restaurant-menu-print-mode');

    if (request.autoPrint) {
      window.setTimeout(function () {
        window.print();
      }, 250);
    }
  }

  document.addEventListener('shopify:section:load', setupJumpNavigation);
  document.addEventListener('DOMContentLoaded', function () {
    setupJumpNavigation();

    const request = getPrintRequest();
    if (!request) return;

    const root = document.querySelector('[data-restaurant-menu]');
    if (root) preparePrint(root, request);
  });
})();
